import React, { useState, useEffect } from 'react';
import { branchAPI, userAPI, holidayGroupAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import BranchCalendar from '../components/BranchCalendar';
import styled from 'styled-components';

const PageContainer = styled.div`
  width: 100%;
  margin: 0;
  padding: ${props => props.theme.spacing.lg};
  box-sizing: border-box;
  max-width: none;

  > h1 {
    margin-bottom: ${props => props.theme.spacing.lg};
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  margin-bottom: 24px;
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 10px 16px;
  cursor: pointer;
  font-family: ${props => props.theme.fonts.main};
  font-size: 0.92rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.secondary};
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color 0.15s ease, border-color 0.15s ease;

  &.active {
    border-bottom-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
  }

  &:hover {
    color: ${props => props.theme.colors.text.primary};
  }
`;

const Card = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.shadows.card};
  padding: 20px;
  margin-bottom: 20px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid ${props => props.theme.colors.border.light};
  }

  th {
    background: ${props => props.theme.colors.surfaceAlt};
    color: ${props => props.theme.colors.text.secondary};
    font-weight: 600;
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  td {
    color: ${props => props.theme.colors.text.primary};
    font-size: 0.92rem;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.7;
  background: ${props => props.theme.colors.surfaceAlt};
  color: ${props => props.theme.colors.text.secondary};
  border: 1px solid ${props => props.theme.colors.border.light};

  &.admin { background: #930c0018; color: #930c00; border-color: #930c0033; }
  &.store_manager,
  &.manager_assistant { background: #e91e6318; color: #c2185b; border-color: #e91e6333; }
  &.makeup,
  &.makeup_supervisor { background: #9c27b018; color: #9c27b0; border-color: #9c27b033; }
  &.photographer,
  &.photographer_supervisor { background: #2196f318; color: #1976d2; border-color: #2196f333; }
  &.retoucher,
  &.retoucher_supervisor { background: #4caf5018; color: #2e7d32; border-color: #4caf5033; }

  &.moveable { background: #2e7d5b18; color: #2e7d5b; border-color: #2e7d5b33; margin-left: 4px; }
  &.holiday-a { background: #3a6ea518; color: #3a6ea5; border-color: #3a6ea533; margin-left: 4px; }
  &.holiday-b { background: #6b4a9e18; color: #6b4a9e; border-color: #6b4a9e33; margin-left: 4px; }
`;

const BranchManagementPage = () => {
  const [activeTab, setActiveTab] = useState('calendar');
  const [branches, setBranches] = useState([]);
  const [branchStaff, setBranchStaff] = useState({});
  const [holidayGroups, setHolidayGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const { hasPermission, accessibleBranches } = useAuth();

  useEffect(() => {
    fetchBranches();
    fetchHolidayGroups();
  }, []);

  useEffect(() => {
    if (branches.length > 0) {
      fetchAllBranchStaff();
    }
  }, [branches]);

  const fetchBranches = async () => {
    try {
      const response = await branchAPI.getAll();
      // For admins and managers, show all branches
      if (accessibleBranches === 'ALL_BRANCHES') {
        setBranches(response.data);
      } else {
        // Filter to only show accessible branches for other roles
        const accessible = response.data.filter(branch => 
          accessibleBranches.includes(branch.id)
        );
        setBranches(accessible);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchHolidayGroups = async () => {
    try {
      const response = await holidayGroupAPI.getAll();
      setHolidayGroups(response.data);
    } catch (error) {
      console.error('Error fetching holiday groups:', error);
    }
  };

  const fetchAllBranchStaff = async () => {
    setLoading(true);
    try {
      const staffData = {};
      for (const branch of branches) {
        const response = await branchAPI.getStaff(branch.id);
        staffData[branch.id] = response.data;
      }
      setBranchStaff(staffData);
    } catch (error) {
      console.error('Error fetching staff data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getHolidayGroupName = (holidayGroupId) => {
    const group = holidayGroups.find(g => g.id === holidayGroupId);
    return group ? group.name : 'None';
  };

  const renderCalendarTab = () => (
    <BranchCalendar />
  );

  const renderOverviewTab = () => (
    <div>
      <h3>Branch Overview</h3>
      <Grid>
        {branches.map(branch => (
          <Card key={branch.id}>
            <h4>{branch.name}</h4>
            <p><strong>Location:</strong> {branch.location || 'Not specified'}</p>
            <p><strong>Status:</strong> {branch.is_active ? 'Active' : 'Inactive'}</p>
            
            <h5>Staff Summary</h5>
            {branchStaff[branch.id] ? (
              <div>
                <p><strong>Total Staff:</strong> {branchStaff[branch.id].length}</p>
                <div style={{ marginTop: '10px' }}>
                  {['admin', 'store_manager', 'manager_assistant', 'makeup', 'makeup_supervisor', 'photographer', 'photographer_supervisor', 'retoucher', 'retoucher_supervisor'].map(role => {
                    const roleStaff = branchStaff[branch.id].filter(user => user.role === role);
                    if (roleStaff.length === 0) return null;
                    return (
                      <div key={role} style={{ marginBottom: '5px' }}>
                        <Badge className={role}>{role}</Badge>
                        <span style={{ marginLeft: '8px' }}>{roleStaff.length}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p>Loading staff...</p>
            )}
          </Card>
        ))}
      </Grid>
    </div>
  );

  const renderStaffTab = () => (
    <div>
      <h3>Staff by Branch</h3>
      {branches.map(branch => (
        <Card key={branch.id}>
          <h4>{branch.name}</h4>
          {branchStaff[branch.id] ? (
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Holiday Group</th>
                  <th>Type</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {branchStaff[branch.id].map(user => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>
                      <Badge className={user.role}>{user.role}</Badge>
                    </td>
                    <td>
                      <Badge className={`holiday-${getHolidayGroupName(user.holiday_group_id).toLowerCase()}`}>
                        Group {getHolidayGroupName(user.holiday_group_id)}
                      </Badge>
                    </td>
                    <td>
                      {user.is_moveable ? (
                        <Badge className="moveable">Moveable</Badge>
                      ) : (
                        <span>Fixed</span>
                      )}
                    </td>
                    <td>{user.email}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p>Loading staff...</p>
          )}
        </Card>
      ))}
    </div>
  );

  const renderHolidaysTab = () => (
    <div>
      <h3>Holiday Groups</h3>
      <Grid>
        {holidayGroups.map(group => (
          <Card key={group.id}>
            <h4>Group {group.name}</h4>
            <p>{group.description}</p>
            
            <h5>Staff in this group</h5>
            <div>
              {Object.values(branchStaff).flat().filter(user => 
                user.holiday_group_id === group.id
              ).map(user => (
                <div key={user.id} style={{ marginBottom: '5px' }}>
                  <Badge className={user.role}>{user.role}</Badge>
                  <span style={{ marginLeft: '8px' }}>{user.name}</span>
                  {user.is_moveable && <Badge className="moveable">Moveable</Badge>}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </Grid>
    </div>
  );

  const tabs = [
    { id: 'calendar', label: 'Calendar', component: renderCalendarTab },
    { id: 'overview', label: 'Overview', component: renderOverviewTab },
    { id: 'staff', label: 'Staff', component: renderStaffTab },
    { id: 'holidays', label: 'Holiday Groups', component: renderHolidaysTab },
  ];

  return (
    <PageContainer>
      <h1>Branch Management</h1>
      
      <TabContainer>
        {tabs.map(tab => (
          <Tab
            key={tab.id}
            className={activeTab === tab.id ? 'active' : ''}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </Tab>
        ))}
      </TabContainer>

      {tabs.find(tab => tab.id === activeTab)?.component()}
      
      {loading && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading...
        </div>
      )}
    </PageContainer>
  );
};

export default BranchManagementPage;